import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; disabled?: boolean }[];
  className?: string;
  openUpward?: boolean;
  showChevron?: boolean;
  disabled?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ 
  value, 
  onChange, 
  options, 
  className = '',
  openUpward = false,
  showChevron = true,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number; adjustedLeft: number }>({ top: 0, left: 0, width: 0, adjustedLeft: 0 });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (selectRef.current && !selectRef.current.contains(target)) {
        // Also check if click is on the portal dropdown
        const portalDropdown = document.querySelector('[data-custom-select-dropdown]');
        if (portalDropdown && !portalDropdown.contains(target)) {
          setIsOpen(false);
        } else if (!portalDropdown) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Update position when opening
      if (selectRef.current) {
        const rect = selectRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const buttonWidth = rect.width;
        const leftPosition = rect.left;
        
        // Calculate minimum width needed for content
        // Find the longest option label
        const maxOptionLength = Math.max(...options.map(opt => opt.label.length));
        // Estimate width: ~7px per character for text-sm, plus padding (24px on each side = 48px)
        const estimatedContentWidth = maxOptionLength * 7 + 48;
        // Use button width as base, but allow expansion up to 1.5x for longer content, with reasonable max
        const minDropdownWidth = Math.min(
          Math.max(buttonWidth, Math.min(estimatedContentWidth, buttonWidth * 1.5)),
          viewportWidth - 16
        );
        
        // Check if dropdown would overflow the right edge of viewport
        const wouldOverflowRight = leftPosition + minDropdownWidth > viewportWidth;
        
        // Calculate adjusted left position
        let adjustedLeft = leftPosition;
        if (wouldOverflowRight) {
          // Shift left to keep dropdown within viewport, with a small margin
          adjustedLeft = Math.max(8, viewportWidth - minDropdownWidth - 8);
        }
        
        setDropdownPosition({
          top: rect.top, // Viewport top position
          left: leftPosition, // Original left position
          width: minDropdownWidth, // Use calculated width instead of button width
          adjustedLeft: adjustedLeft // Adjusted left position to prevent overflow
        });
      }
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, openUpward, options]);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div className="relative w-full" ref={selectRef} style={{ zIndex: isOpen ? 9999 : 'auto' }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-3 py-2 rounded-lg bg-white text-sm text-left flex items-center gap-2 min-w-[50px] focus:outline-none focus:ring-0 ${className ? className : 'border border-accent-taupe'} ${
          disabled ? 'bg-accent-sand text-text-description cursor-not-allowed' : ''
        }`}
      >
        <span className="flex-1 truncate">{selectedOption.label}</span>
        {showChevron && (
          <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-xs text-text-description flex-shrink-0`}></i>
        )}
      </button>

      {isOpen && createPortal(
        <div 
          data-custom-select-dropdown
          className="fixed z-[9999] bg-white border border-accent-taupe rounded-lg shadow-xl max-h-60 overflow-y-auto outline-none ring-0"
          style={{
            [openUpward ? 'bottom' : 'top']: openUpward 
              ? `${window.innerHeight - dropdownPosition.top}px`
              : `${dropdownPosition.top + (selectRef.current?.getBoundingClientRect().height || 0)}px`,
            left: `${dropdownPosition.adjustedLeft || dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                if (!option.disabled) {
                  onChange(option.value);
                  setIsOpen(false);
                }
              }}
              disabled={option.disabled}
              className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                value === option.value
                  ? 'bg-secondary-500 text-white'
                  : option.disabled
                    ? 'text-text-description cursor-not-allowed opacity-50'
                    : 'text-text-heading hover:bg-secondary-100'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomSelect;
